import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { EvidenceBadge } from "@/components/common/EvidenceBadge";

const APPROVAL_KEYS = [
  ["public_client_name_allowed", "Public client name allowed?"],
  ["public_technology_name_allowed", "Public technology name allowed?"],
  ["dbrf_contribution_approved", "DBRF contribution approved?"],
  ["rights_statement_approved", "Rights statement approved?"],
  ["evidence_state_approved", "Evidence state approved?"],
  ["public_source_approved", "Public source approved?"],
  ["legal_confidentiality_review_complete", "Legal / confidentiality review complete?"],
];

export default function AdminProjects() {
  const { user } = useOutletContext();
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);
  const isAdmin = user.role === "administrator";

  const load = () => api.get("/admin/content/projects").then(({ data }) => {
    setRows(data.results);
    setActive((a) => (a ? data.results.find((r) => r.slug === a.slug) || null : null));
  });
  useEffect(() => { load(); }, []);

  const togglePublish = async (p) => {
    try {
      await api.patch(`/admin/content/projects/${p.slug}`, null, { params: { published: !p.published } });
      toast.success(!p.published ? "Published" : "Unpublished");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Action failed");
    }
  };

  const setApproval = async (p, key, val) => {
    try {
      await api.patch(`/admin/content/projects/${p.slug}/approvals`, { approvals: { [key]: val } });
      toast.success("Approval updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    }
  };

  return (
    <div>
      <h1 className="font-display text-3xl font-bold text-navy">OEM projects</h1>
      <p className="mt-1 text-sm text-[var(--mineral)]">Publication requires all approvals. Confidential fields are never exposed to public APIs.</p>
      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_1.1fr]">
        <div className="flex flex-col gap-3">
          {rows.map((p) => (
            <button key={p.slug} onClick={() => setActive(p)} data-testid={`admin-project-${p.slug}`}
              className={`rounded-2xl border bg-[var(--paper)] p-5 text-left ${active?.slug === p.slug ? "border-[var(--cobalt)]" : "border-[var(--line)]"}`}>
              <div className="flex items-center justify-between">
                <span className={`rounded-full px-2 py-0.5 font-mono text-[0.66rem] ${p.published ? "bg-[var(--success)]/15 text-[var(--success)]" : "bg-[var(--mineral)]/15 text-[var(--mineral)]"}`}>{p.published ? "Published" : "Draft"}</span>
                {p.preview_warnings?.length > 0 && <span className="flex items-center gap-1 font-mono text-[0.66rem] text-[var(--warning)]"><AlertTriangle size={12} /> {p.preview_warnings.length}</span>}
              </div>
              <p className="mt-2 font-display font-semibold text-navy">{p.public_title}</p>
              <p className="text-xs text-[var(--mineral)]">{p.technology_family} · {p.project_stage}</p>
            </button>
          ))}
        </div>

        {active && (
          <div className="rounded-2xl border border-[var(--line)] bg-[var(--paper)] p-6" data-testid="admin-project-detail">
            <div className="flex flex-wrap items-center gap-2">
              <EvidenceBadge status={active.evidence_status} />
              <span className="font-mono text-xs text-[var(--mineral)]">{active.slug}</span>
            </div>
            <h2 className="mt-3 font-display text-xl font-bold text-navy">{active.public_title}</h2>

            {active.preview_warnings?.length > 0 && (
              <div className="mt-4 rounded-xl border border-[var(--warning)]/30 bg-[var(--warning)]/10 p-4" data-testid="preview-warnings">
                <p className="eyebrow mb-2 !text-[var(--warning)]">Preview warnings</p>
                <ul className="list-inside list-disc text-sm text-[var(--warning)]">
                  {active.preview_warnings.map((w) => <li key={w}>{w}</li>)}
                </ul>
                <p className="mt-2 text-xs text-[var(--mineral)]">Warnings require evidence review; they do not automatically ban correct content.</p>
              </div>
            )}

            <div className="mt-5 border-t border-[var(--line)] pt-4">
              <p className="eyebrow mb-3">Publication checklist {!isAdmin && "(administrator can edit)"}</p>
              <div className="space-y-2">
                {APPROVAL_KEYS.map(([k, label]) => (
                  <label key={k} className="flex items-center justify-between gap-3 text-sm">
                    <span className="text-navy">{label}</span>
                    <input type="checkbox" disabled={!isAdmin} checked={!!active.approvals?.[k]} onChange={(e) => setApproval(active, k, e.target.checked)} data-testid={`approval-${k}`} className="h-4 w-4 accent-[var(--cobalt)]" />
                  </label>
                ))}
              </div>
            </div>

            <div className="mt-5 flex items-center gap-3 border-t border-[var(--line)] pt-4">
              <button onClick={() => togglePublish(active)} data-testid="admin-project-publish" className="rounded-full bg-[var(--navy)] px-5 py-2.5 text-sm font-semibold text-white btn-transition hover:bg-[var(--cobalt)]">
                {active.published ? "Unpublish" : "Publish"}
              </button>
              {active.publish_blocked && !active.published && <span className="font-mono text-xs text-[var(--warning)]">Complete approvals to publish.</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
